import React from 'react';
import { ExecutiveBioData } from './types';

interface BioTemplate1Props {
  data: ExecutiveBioData;
  margins?: { top: number; bottom: number; left: number; right: number };
}

// Template 1: Professional Classic - Serif fonts, gray borders, centered header
export const BioTemplate1: React.FC<BioTemplate1Props> = ({ data, margins }) => {
  const defaultMargins = { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 };
  const m = margins || defaultMargins;
  
  return (
    <div 
      className="bg-white text-gray-900 font-serif text-[11pt] leading-relaxed"
      style={{
        width: '8.5in',
        minHeight: '11in',
        padding: `${m.top}in ${m.right}in ${m.bottom}in ${m.left}in`,
        boxSizing: 'border-box'
      }}
    >
      {/* Header - Centered */}
      <header className="text-center border-b border-gray-400 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 tracking-wide">
          {data.name.toUpperCase()}
        </h1>
        {data.title && (
          <h2 className="text-base text-gray-700 mb-2">{data.title}</h2>
        )}
        <div className="text-xs text-gray-600 leading-tight">
          {[data.phone, data.city && data.state ? `${data.city}, ${data.state}` : data.city, data.email, data.linkedIn]
            .filter(Boolean)
            .join(' • ')}
        </div>
      </header>

      {/* Section Title */}
      <h3 className="text-center text-sm font-bold text-gray-900 mb-4 tracking-wide border-t border-b border-gray-400 py-2">
        EXECUTIVE BIOGRAPHY
      </h3>

      {/* Bio Paragraphs */}
      <div className="space-y-4 text-sm text-justify mb-6">
        {data.bioParagraphs.map((paragraph, index) => (
          <p key={index} className="leading-relaxed text-gray-800">
            {paragraph}
          </p>
        ))}
      </div>

      {/* Key Accomplishments */}
      {data.keyAccomplishments && data.keyAccomplishments.length > 0 && (
        <section className="mb-6">
          <h3 className="text-center text-sm font-bold text-gray-900 mb-3 tracking-wide border-t border-b border-gray-400 py-2">
            KEY ACCOMPLISHMENTS
          </h3>
          <ul className="list-disc list-inside text-sm text-gray-800 space-y-2 ml-4">
            {data.keyAccomplishments.map((item, index) => (
              <li key={index} className="leading-relaxed">{item}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Expertise */}
      {data.expertise && data.expertise.length > 0 && (
        <section>
          <h3 className="text-center text-sm font-bold text-gray-900 mb-3 tracking-wide border-t border-b border-gray-400 py-2">
            AREAS OF EXPERTISE
          </h3>
          <p className="text-xs text-gray-800 text-center leading-snug">
            {data.expertise.join(' • ')}
          </p>
        </section>
      )}
    </div>
  );
};
